import { useCallback, useEffect, useMemo, useState } from "react";
import type React from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ChevronRight, Clock } from "lucide-react";
import { api } from "../../api/client";
import { theme, card, btnPrimary, btnSecondary, emptyFrame, floatingSheet } from "../../theme";
import { rankActions } from "../ActionsPage";
import { useConsultantShell } from "./ConsultantShellContext";
import { ScopeToggle } from "./ScopeToggle";
import { addDaysKey, errText, todayKey } from "./workspace";

// ── Today ─────────────────────────────────────────────────────────────────
// One ranked list of what needs the consultant (backend lib/consultantActions.ts).
// Rows are acted on where they stand — confirm a booking, approve a client's SOA
// drafts, tick a follow-up off — or put aside until a date. Everything a row does
// is re-checked by the API; a row only offers what its item says it can do.

type Inline = "confirm_slot" | "decline_slot" | "approve_soa_drafts" | "complete_follow_up";

export interface ConsultantAction {
  id: string;
  type: string;
  title: string;
  description: string;
  href: string;
  severity: "high" | "medium" | "low";
  dueAt: string | null;
  organizationId: string | null;
  inline?: Inline[];
}

interface ActionsResponse {
  total: number;
  items: ConsultantAction[];
  hidden?: Record<string, number>;
  snoozed?: Array<{ item: ConsultantAction; until: string }>;
  scope?: "mine" | "firm";
}

export const ACTION_TYPE_LABELS: Record<string, string> = {
  pending_slot:      "Booking requests",
  tier_request:      "Tier upgrade requests",
  clause_review:     "Clause reviews",
  soa_approval:      "SOA approvals",
  dpia_review:       "DPIA reviews",
  sector_unresolved: "Regulated sector",
  follow_up:         "Follow-ups",
  upcoming_meeting:  "Meetings",
  upcoming_review:   "Reviews",
  upcoming_audit:    "Audits",
  renewal_due:       "Renewals",
};

const SEVERITY_STYLE: Record<string, { bg: string; text: string; label: string }> = {
  high:   { bg: theme.color.dangerBg,  text: theme.color.dangerText,  label: "Urgent" },
  medium: { bg: theme.color.warningBg, text: theme.color.warningText, label: "Action" },
  low:    { bg: theme.color.accentBg,  text: theme.color.accentText,  label: "Review" },
};

const SNOOZE_OPTIONS = [
  { label: "Tomorrow", days: 1 },
  { label: "In 3 days", days: 3 },
  { label: "Next week", days: 7 },
  { label: "In 2 weeks", days: 14 },
];

const typeLabel = (t: string) => ACTION_TYPE_LABELS[t] ?? t.replace(/_/g, " ");

export function TodayPage() {
  const navigate = useNavigate();
  const { scope, portfolio } = useConsultantShell();
  const [data, setData] = useState<ActionsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState<"" | "high" | "medium" | "low">("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [snoozeOpen, setSnoozeOpen] = useState<string | null>(null);
  const [showSnoozed, setShowSnoozed] = useState(false);

  const load = useCallback(() => {
    setError(null);
    api.get<ActionsResponse>("/consultant/actions", { scope })
      .then((d) => { setData(d); setSelected(new Set()); })
      .catch((e) => setError(errText(e, "Could not load your actions")));
  }, [scope]);
  useEffect(load, [load]);

  const items = useMemo(() => rankActions(data?.items ?? []), [data]);
  const clientNames = useMemo(() => {
    // Names from the portfolio: an item's description does not always start with the client's name
    // (a clause review starts with the control's).
    const names = new Map((portfolio.orgs ?? []).map((o) => [o.id, o.name] as const));
    const m = new Map<string, string>();
    for (const i of items) if (i.organizationId) m.set(i.organizationId, names.get(i.organizationId) ?? i.description.split(" · ")[0]);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [items, portfolio.orgs]);
  const types = useMemo(() => [...new Set(items.map((i) => i.type))].sort((a, b) => typeLabel(a).localeCompare(typeLabel(b))), [items]);

  const shown = items.filter((i) =>
    (!typeFilter || i.type === typeFilter) &&
    (!clientFilter || i.organizationId === clientFilter) &&
    (!severityFilter || i.severity === severityFilter));

  async function act(key: string, fn: () => Promise<unknown>, fallback: string) {
    setBusy(key);
    setActionError(null);
    try { await fn(); load(); }
    catch (e) { setActionError(errText(e, fallback)); }
    finally { setBusy(null); }
  }

  const runInline = (item: ConsultantAction, what: Inline) => {
    switch (what) {
      case "confirm_slot":
        return act(`${item.id}:${what}`, () => api.patch(`/consultant/slots/${item.id}`, { action: "confirmed" }), "Could not confirm the booking");
      case "decline_slot":
        return act(`${item.id}:${what}`, () => api.patch(`/consultant/slots/${item.id}`, { action: "declined" }), "Could not decline the booking");
      case "approve_soa_drafts":
        return act(`${item.id}:${what}`, () => api.put(`/consultant/clients/${item.organizationId}/soa/approve-all`), "Could not approve the drafts");
      case "complete_follow_up":
        return act(`${item.id}:${what}`, () => api.patch(`/consultant/workspace/notes/${item.id.replace(/^follow_up:/, "")}`, { followUpDone: true }), "Could not mark the follow-up done");
    }
  };

  const snooze = (item: ConsultantAction, days: number) => {
    setSnoozeOpen(null);
    return act(`${item.id}:snooze`, () => api.post("/consultant/workspace/snoozes", { itemId: item.id, until: addDaysKey(todayKey(), days) }), "Could not snooze the item");
  };
  const unsnooze = (itemId: string) =>
    act(`${itemId}:unsnooze`, () => api.del("/consultant/workspace/snoozes", { itemId }), "Could not bring the item back");

  const approvable = shown.filter((i) => i.inline?.includes("approve_soa_drafts"));
  const approveSelected = () =>
    act("bulk", async () => {
      const targets = approvable.filter((i) => selected.has(i.id));
      const failures: string[] = [];
      for (const t of targets) {
        try { await api.put(`/consultant/clients/${t.organizationId}/soa/approve-all`); }
        catch { failures.push(t.description.split(" · ")[0]); }
      }
      if (failures.length) throw new Error(`Could not approve drafts for ${failures.join(", ")}`);
    }, "Could not approve the selected drafts");

  const open = (item: ConsultantAction) => {
    if (item.href.startsWith("/consultant/")) navigate(item.href);
  };

  const select: React.CSSProperties = { padding: "6px 8px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: theme.color.panel, color: theme.color.text, fontSize: 12.5, maxWidth: 220 };
  const pill = (active: boolean): React.CSSProperties => ({
    padding: "5px 11px", borderRadius: 3, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: theme.font,
    border: `1px solid ${active ? theme.color.accent : theme.color.border}`,
    background: active ? theme.color.accentBg : "transparent",
    color: active ? theme.color.accentText : theme.color.subtle,
  });
  const smallBtn: React.CSSProperties = { ...btnSecondary, padding: "4px 10px", fontSize: 11.5 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: theme.color.heading, marginBottom: 4 }}>Today</div>
          <div style={{ fontSize: 13, color: theme.color.subtle }}>What needs you, most urgent first. Act on a row where it is, open it, or put it aside until later.</div>
        </div>
        <ScopeToggle />
      </div>

      {error && <div style={{ fontSize: 13, color: theme.color.dangerText }}>{error} <button onClick={load} style={smallBtn}>Retry</button></div>}
      {!data && !error && <div style={{ fontSize: 13, color: theme.color.subtle }}>Loading…</div>}

      {data && (
        <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {(["", "high", "medium", "low"] as const).map((s) => (
              <button key={s || "all"} onClick={() => setSeverityFilter(s)} style={pill(severityFilter === s)}>
                {s ? SEVERITY_STYLE[s].label : "All"}
                <span style={{ marginLeft: 6, opacity: 0.7 }}>{s ? items.filter((i) => i.severity === s).length : items.length}</span>
              </button>
            ))}
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} aria-label="Filter by type" style={select}>
              <option value="">Every kind</option>
              {types.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
            </select>
            <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} aria-label="Filter by client" style={select}>
              <option value="">Every client</option>
              {clientNames.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
            {approvable.length > 1 && (
              <button
                onClick={approveSelected}
                disabled={selected.size === 0 || busy === "bulk"}
                style={{ ...btnPrimary, padding: "6px 12px", fontSize: 12, marginLeft: "auto", opacity: selected.size === 0 || busy === "bulk" ? 0.5 : 1 }}
              >
                {busy === "bulk" ? "Approving…" : `Approve SOA drafts (${selected.size})`}
              </button>
            )}
          </div>

          {Object.entries(data.hidden ?? {}).map(([type, n]) => (
            <div key={type} style={{ fontSize: 12.5, color: theme.color.subtle }}>
              Showing {items.filter((i) => i.type === type).length} of {items.filter((i) => i.type === type).length + n} {typeLabel(type).toLowerCase()} — open a client to see all of theirs.
            </div>
          ))}
          {actionError && <div role="alert" style={{ fontSize: 12.5, color: theme.color.dangerText }}>{actionError}</div>}

          {data.total === 0 ? (
            <div style={{ ...emptyFrame, padding: "40px 24px" }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}><CheckCircle2 size={28} color={theme.color.success} /></div>
              <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.successText, marginBottom: 4 }}>All clear</div>
              <div style={{ fontSize: 13, color: theme.color.subtle }}>
                {scope === "mine" ? "Nothing outstanding on your clients. Switch to Whole firm to see everyone's." : "No outstanding actions across your portfolio."}
              </div>
            </div>
          ) : shown.length === 0 ? (
            <div style={{ ...emptyFrame, color: theme.color.subtle, fontSize: 13, padding: 28 }}>Nothing matches these filters.</div>
          ) : (
            <div style={{ ...card, padding: 0, overflow: "visible" }}>
              {shown.map((item, idx) => {
                const sev = SEVERITY_STYLE[item.severity] ?? SEVERITY_STYLE.low;
                const canSelect = item.inline?.includes("approve_soa_drafts") && approvable.length > 1;
                return (
                  <div key={item.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 16px", borderBottom: idx < shown.length - 1 ? `1px solid ${theme.color.border}` : "none", position: "relative" }}>
                    <div style={{ width: 18, paddingTop: 2, flexShrink: 0 }}>
                      {canSelect && (
                        <input type="checkbox" aria-label={`Select ${item.description}`} checked={selected.has(item.id)}
                          onChange={(e) => setSelected((s) => { const n = new Set(s); if (e.target.checked) n.add(item.id); else n.delete(item.id); return n; })} />
                      )}
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", background: sev.bg, color: sev.text, borderRadius: 3, padding: "3px 7px", flexShrink: 0, marginTop: 1 }}>{sev.label}</span>
                    <button onClick={() => open(item)} style={{ flex: 1, minWidth: 0, textAlign: "left", background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: theme.font }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, marginBottom: 2 }}>{item.title}</div>
                      <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>{item.description}</div>
                      <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 3 }}>
                        {typeLabel(item.type)}
                        {item.dueAt && ` · due ${new Date(item.dueAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`}
                      </div>
                    </button>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      {item.inline?.includes("confirm_slot") && (
                        <button disabled={!!busy} onClick={() => runInline(item, "confirm_slot")} style={{ ...btnPrimary, padding: "4px 10px", fontSize: 11.5 }}>
                          {busy === `${item.id}:confirm_slot` ? "…" : "Confirm"}
                        </button>
                      )}
                      {item.inline?.includes("decline_slot") && (
                        <button disabled={!!busy} onClick={() => runInline(item, "decline_slot")} style={smallBtn}>Decline</button>
                      )}
                      {item.inline?.includes("approve_soa_drafts") && (
                        <button disabled={!!busy} onClick={() => runInline(item, "approve_soa_drafts")} style={smallBtn} title="Approve every draft SOA entry for this client">
                          {busy === `${item.id}:approve_soa_drafts` ? "…" : "Approve all"}
                        </button>
                      )}
                      {item.inline?.includes("complete_follow_up") && (
                        <button disabled={!!busy} onClick={() => runInline(item, "complete_follow_up")} style={smallBtn}>Done</button>
                      )}
                      <div style={{ position: "relative" }}>
                        <button aria-haspopup="menu" aria-expanded={snoozeOpen === item.id} onClick={() => setSnoozeOpen((o) => (o === item.id ? null : item.id))}
                          title="Put aside until later" style={{ ...smallBtn, display: "flex", alignItems: "center", gap: 4 }}>
                          <Clock size={12} /> Later
                        </button>
                        {snoozeOpen === item.id && (
                          <div role="menu" style={{ position: "absolute", right: 0, top: "calc(100% + 4px)", zIndex: 20, ...floatingSheet, padding: 4, minWidth: 130 }}>
                            {SNOOZE_OPTIONS.map((o) => (
                              <button key={o.days} role="menuitem" onClick={() => snooze(item, o.days)}
                                style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "6px 10px", fontSize: 12.5, color: theme.color.text, cursor: "pointer", borderRadius: 3, fontFamily: theme.font }}>
                                {o.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <ChevronRight size={14} color={theme.color.faint} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {(data.snoozed?.length ?? 0) > 0 && (
            <div>
              <button onClick={() => setShowSnoozed((s) => !s)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: theme.color.accentText, fontFamily: theme.font }}>
                {showSnoozed ? "Hide" : "Show"} {data.snoozed!.length} put aside
              </button>
              {showSnoozed && (
                <div style={{ ...card, padding: 0, marginTop: 8 }}>
                  {data.snoozed!.map(({ item, until }, idx) => (
                    <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderBottom: idx < data.snoozed!.length - 1 ? `1px solid ${theme.color.border}` : "none" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: theme.color.text }}>{item.title}</div>
                        <div style={{ fontSize: 11.5, color: theme.color.subtle }}>{item.description} · back on {new Date(until).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</div>
                      </div>
                      <button disabled={!!busy} onClick={() => unsnooze(item.id)} style={smallBtn}>Bring back</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
